import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, ExternalLink } from 'lucide-react';
import { adminApi } from '../api';
import { Modal } from '../components/Modal';
import { TextInput, I18nInput } from '../components/Field';

function TenantRow({ tenant, index, onToggle, onEdit, onRemove }) {
  return (
    <div data-testid={`tenant-row-${tenant.id}`} className={`flex items-center gap-4 p-4 ${index > 0 ? 'border-t border-paelbi-line' : ''}`}>
      <div className="h-9 w-9 rounded-lg" style={{ background: tenant.theme?.brown || '#8B6F4E' }} />
      <div className="flex-1">
        <p className="font-medium text-paelbi-ink">{tenant.brand?.name} <span className="text-xs text-paelbi-ink/50">/{tenant.slug}</span></p>
        <p className="text-xs text-paelbi-ink/50">Piano: {tenant.plan} {'\u00b7'} {tenant.active ? 'Attivo' : 'Disattivato'}</p>
      </div>
      <a href={`/${tenant.slug}`} target="_blank" rel="noreferrer" className="rounded-lg p-2 text-paelbi-ink/60 hover:bg-paelbi-bg" title="Vedi sito"><ExternalLink size={16} /></a>
      <button type="button" onClick={() => onToggle(tenant)} className="rounded-lg border border-paelbi-line px-3 py-1.5 text-xs">{tenant.active ? 'Disattiva' : 'Attiva'}</button>
      <button type="button" data-testid={`tenant-edit-${tenant.id}`} onClick={() => onEdit(tenant)} className="rounded-lg p-2 text-paelbi-ink/60 hover:bg-paelbi-bg"><Pencil size={16} /></button>
      <button type="button" data-testid={`tenant-delete-${tenant.id}`} onClick={() => onRemove(tenant)} className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
    </div>
  );
}

function TenantFormModal({ editing, saving, onChange, onClose, onSave }) {
  const setField = (patch) => onChange({ ...editing, ...patch });
  const setBrand = (patch) => onChange({ ...editing, brand: { ...editing.brand, ...patch } });
  const setContacts = (patch) => onChange({ ...editing, contacts: { ...editing.contacts, ...patch } });
  return (
    <Modal open={!!editing} onClose={onClose} title={editing?.id ? 'Modifica struttura' : 'Nuova struttura'}
      footer={<>
        <button type="button" onClick={onClose} className="rounded-btn border border-paelbi-line px-4 py-2 text-sm">Annulla</button>
        <button type="button" data-testid="tenant-save-button" onClick={onSave} disabled={saving} className="rounded-btn bg-paelbi-brown px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Salvo...' : 'Salva'}</button>
      </>}>
      {editing && (
        <div className="space-y-4">
          {!editing.id && <TextInput label="Slug (url)" value={editing.slug} onChange={(v) => setField({ slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="es. villa-mare" />}
          <TextInput label="Nome brand" value={editing.brand?.name} onChange={(v) => setBrand({ name: v })} />
          <I18nInput label="Payoff" value={editing.brand?.payoff} onChange={(v) => setBrand({ payoff: v })} />
          <TextInput label="Email" value={editing.contacts?.email} onChange={(v) => setContacts({ email: v })} />
          <TextInput label="Telefono" value={editing.contacts?.phone} onChange={(v) => setContacts({ phone: v })} />
        </div>
      )}
    </Modal>
  );
}

export function Tenants() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.listTenants().then(setList);
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ slug: '', brand: { name: 'PAELBI', payoff: 'IL TUO B&B' }, contacts: {} });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await adminApi.updateTenant(editing.id, { brand: editing.brand, contacts: editing.contacts });
      } else {
        await adminApi.createTenant({ slug: editing.slug, brand: editing.brand, contacts: editing.contacts });
      }
      setEditing(null); await load();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (t) => { await adminApi.updateTenant(t.id, { active: !t.active }); load(); };
  const remove = async (t) => {
    if (window.confirm(`Eliminare ${t.slug}? Verranno rimossi tutti i contenuti.`)) { await adminApi.deleteTenant(t.id); load(); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-paelbi-ink">Strutture</h1>
          <p className="text-paelbi-ink/60">Gestisci i tenant della piattaforma.</p>
        </div>
        <button type="button" data-testid="tenant-new-button" onClick={openNew} className="inline-flex items-center gap-2 rounded-btn bg-paelbi-brown px-4 py-2.5 text-sm font-semibold text-white hover:bg-paelbi-brown-dark"><Plus size={16} />Nuova struttura</button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-paelbi-line bg-white" data-testid="tenants-list">
        {list.map((t, i) => (
          <TenantRow key={t.id} tenant={t} index={i} onToggle={toggleActive} onEdit={(x) => setEditing({ ...x })} onRemove={remove} />
        ))}
      </div>

      <TenantFormModal editing={editing} saving={saving} onChange={setEditing} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

export default Tenants;
